import React, { useState, useRef, useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { useStyletron } from 'baseui';
import { TwoWayChatInputBox } from './chat-input-box/RelayMessengerChatInputBox';
import { customTheme } from '../baseweb-custom-theme/customTheme';
import { TranslationMap } from '../chat-item/ChatItem';

export type TwoWayChatInputProps = {
  onNewMessage: (messageVal: string) => void;
  onTyping?: () => void;
  isConversationTranslated?: boolean;
  isRMTranslationsEnabled?: boolean;
  customerLanguage?: string;
  onTranslateMessage?: (text: string) => void;
  translation?: {
    originalMessage: string;
    rawTranslation: string;
    translatedMessage: string;
    edited: boolean;
  };
  setTranslation?: (
    value: {
      originalMessage: string;
      rawTranslation: string;
      translatedMessage: string;
      edited: boolean;
    } | null,
  ) => void;
  onTurnOffTranslation?: () => void;
};

export const TwoWayChatInput = ({
  onNewMessage,
  onTyping,
  isConversationTranslated = false,
  isRMTranslationsEnabled = false,
  customerLanguage,
  onTranslateMessage,
  translation,
  setTranslation,
  onTurnOffTranslation,
}: TwoWayChatInputProps) => {
  const [css] = useStyletron();
  const [inputValue, setInputValue] = useState('');
  const [translateInputValue, setTranslateInputValue] = useState('');
  const translateInputRef = useRef<HTMLTextAreaElement | null>(null);
  const mainInputRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (translation?.translatedMessage) {
      mainInputRef.current?.focus();
    }
  }, [translation?.translatedMessage]);

  const assignedMatch = useMatch('/:clientId/assigned/:channelSid');
  const channelSid = assignedMatch?.params?.channelSid;

  useEffect(() => {
    if (!channelSid) return;
    setInputValue('');
    setTranslateInputValue('');
    setTranslation?.(null);
  }, [channelSid]);

  return (
    <div>
      {isConversationTranslated && isRMTranslationsEnabled && (
        <>
          <div
            className={css({
              fontSize: customTheme.typography.LabelSmall.fontSize,
              color: customTheme.colors.contentSecondary,
              padding: '2px 0px',
              textAlign: 'center',
            })}
            data-testid="translation-indicator"
          >
            You are currently translating outgoing messages from English
            {customerLanguage ? ` to ${TranslationMap[customerLanguage]} ` : ''}
            .
            <span
              className={css({
                color: customTheme.colors.chatStampButtonText,
                cursor: 'pointer',
                fontWeight: 700,
              })}
              onClick={onTurnOffTranslation}
              data-testid="turn-off-translation-action"
            >
              Turn off
            </span>
          </div>
          <TwoWayChatInputBox
            inputValue={translateInputValue}
            onChange={setTranslateInputValue}
            submitButtonText="Translate"
            onSubmit={onTranslateMessage!}
            onTyping={onTyping}
            maxLength={2000}
            variant="secondary"
            placeholder="Enter message to be translated"
            textAreaRef={translateInputRef}
            dataTestid="two-way-translate-chat-input"
          />
        </>
      )}
      <TwoWayChatInputBox
        inputValue={
          translation?.translatedMessage && translation.translatedMessage.length
            ? translation.translatedMessage
            : inputValue
        }
        translation={translation}
        setTranslation={setTranslation}
        setTranslateInputValue={setTranslateInputValue}
        onChange={setInputValue}
        submitButtonText="Send"
        onSubmit={onNewMessage}
        onTyping={onTyping}
        maxLength={2000}
        textAreaRef={mainInputRef}
        focusTranslateInput={() => translateInputRef.current?.focus?.()}
        dataTestid="two-way-chat-input"
      />
    </div>
  );
};
